import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Package, Tag, Award, IndianRupee, Users, AlertTriangle, Truck } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export default function AdminDashboard() {
    const { settings } = useSettings();
    const cur = settings?.currency_symbol || "Rs.";
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
    }, []);

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Overview</div>
                <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Dashboard</h1>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KPI label="Orders" value={data?.total_orders ?? 0} Icon={ShoppingCart} testid="kpi-orders" />
                <KPI label="Revenue" value={formatPrice(data?.total_revenue ?? 0, cur)} Icon={IndianRupee} testid="kpi-revenue" />
                <KPI label="Products" value={data?.total_products ?? 0} Icon={Package} testid="kpi-products" />
                <KPI label="Categories" value={data?.total_categories ?? 0} Icon={Tag} testid="kpi-categories" />
                <KPI label="Brands" value={data?.total_brands ?? 0} Icon={Award} testid="kpi-brands" />
                <KPI label="Customers" value={data?.total_customers ?? 0} Icon={Users} testid="kpi-customers" />
            </div>

            {/* Catalog quality — flags products that would charge the Rs.99 fallback. */}
            {data?.catalog_quality && data.catalog_quality.fallback_shipping_count > 0 && (
                <Link
                    to="/admin/catalog/shipping-audit"
                    data-testid="dashboard-shipping-audit-card"
                    className="block bg-[#121214] border border-amber-500/30 rounded-lg p-5 hover:bg-[#1a1a1e] transition-colors"
                >
                    <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded bg-amber-500/15 text-amber-400 flex items-center justify-center flex-shrink-0">
                            <Truck size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-400/80 font-display font-bold">Catalog quality</div>
                            <div className="mt-1 text-white font-bold text-lg">
                                <span data-testid="dashboard-shipping-audit-count" className="text-amber-400">{data.catalog_quality.fallback_shipping_count}</span> active product{data.catalog_quality.fallback_shipping_count === 1 ? "" : "s"} missing weight or variant weight
                            </div>
                            <div className="mt-1 text-sm text-white/55">
                                These products will charge the Rs.99 placeholder shipping on every order. Review and add weights →
                            </div>
                        </div>
                        <AlertTriangle size={16} className="text-amber-400/70 flex-shrink-0" />
                    </div>
                </Link>
            )}

            <div className="grid lg:grid-cols-2 gap-5">
                <Card title="Recent orders" link={{ to: "/admin/orders", label: "All orders →" }}>
                    {(!data || data.recent_orders.length === 0) ? (
                        <Empty>No orders yet.</Empty>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-left text-xs uppercase tracking-widest font-display text-white/55">
                                <tr>
                                    <th className="py-2">Order #</th>
                                    <th className="py-2">Customer</th>
                                    <th className="py-2">Total</th>
                                    <th className="py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.recent_orders.map((o) => (
                                    <tr key={o.id} className="border-t border-[#27272A]">
                                        <td className="py-2">
                                            <Link to={`/admin/orders/${o.id}`} className="text-[#00BCE5] hover:underline">{o.order_number}</Link>
                                        </td>
                                        <td className="py-2 text-white/85">{o.shipping_address?.full_name}</td>
                                        <td className="py-2">{formatPrice(o.total, cur)}</td>
                                        <td className="py-2 uppercase tracking-widest text-xs font-display text-[#00BCE5]">
                                            {o.status}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Card>

                <Card title="Low stock alerts" link={{ to: "/admin/products", label: "Manage →" }}>
                    {(!data || data.low_stock.length === 0) ? (
                        <Empty>All stock levels healthy.</Empty>
                    ) : (
                        <ul className="space-y-2">
                            {data.low_stock.map((s, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm border-b border-[#27272A] pb-2">
                                    <AlertTriangle size={14} className="text-[#FF9F0A] mt-0.5" />
                                    <div className="flex-1">
                                        <Link
                                            to={`/admin/products/${s.product_id}`}
                                            className="text-white hover:text-[#00BCE5]"
                                        >
                                            {s.name}
                                        </Link>
                                        {s.variant && (
                                            <div className="text-xs text-white/55 font-display uppercase tracking-widest">{s.variant}</div>
                                        )}
                                        {s.sku && !/^https?:\/\//i.test(s.sku) && (
                                            <div className="text-[11px] text-white/45">SKU: {s.sku}</div>
                                        )}
                                    </div>
                                    <div className="font-display font-black text-[#FF9F0A]">{s.stock}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
        </div>
    );
}

function KPI({ label, value, Icon, testid }) {
    return (
        <div className="bg-[#18181B] border border-[#27272A] rounded-sm p-4 relative overflow-hidden" data-testid={testid}>
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00BCE5] to-transparent opacity-60" />
            <div className="flex items-center justify-between text-white/55">
                <span className="text-[11px] uppercase tracking-widest font-display font-bold">{label}</span>
                <Icon size={14} className="text-[#00BCE5]" />
            </div>
            <div className="font-display font-black text-2xl md:text-3xl mt-1 text-white">{value}</div>
        </div>
    );
}

function Card({ title, link, children }) {
    return (
        <div className="bg-[#18181B] border border-[#27272A] rounded-sm">
            <div className="px-4 py-3 flex items-center justify-between border-b border-[#27272A]">
                <h3 className="font-display uppercase font-black tracking-tight">{title}</h3>
                {link && (
                    <Link to={link.to} className="text-xs uppercase tracking-widest text-white/55 hover:text-[#00BCE5] font-display">
                        {link.label}
                    </Link>
                )}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function Empty({ children }) {
    return <div className="text-sm text-white/45 py-6 text-center">{children}</div>;
}
